/**
 * accountHealthView — Account detail v3 AD3 (design note §45; spec v3 §3, §4;
 * slice plan v2, ruled 2026-10-31): the WORDS for the server's account health
 * and the deals' rule-set-4 chips. Every verdict is the server's — the six
 * checks, their states, the score, the status, the headline's check and the
 * "Move to" date all arrive on the wire; nothing here decides health or does
 * calendar arithmetic beyond formatting a date the server named.
 */
import type { DealRule, SalesAccountHealth, SalesAccountHealthCheck, SalesAccountSeat } from '../../../../api/types'
import { danger, success, warning } from '../../../../theme/tokens'
import { compactMoney } from '../../../../lib/money'
import { SLUG_TO_STAGE } from '../../api'
import { laneLabel } from './pipelineView'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** A stage slug in the app's words (§43 ruling 2): "qualified" → "Qualified". */
export function stageWords(slug: string | undefined): string {
  if (!slug) return ''
  const s = SLUG_TO_STAGE[slug]
  return s ? laneLabel(s) : slug
}

const n = (k: number, one: string, many: string) => (k === 1 ? `1 ${one}` : `${k} ${many}`)

// ── Chips (Q6; v4 §3's words, plan v2 D4) ────────────────────────────────────

/** One rule's chip words. Unknown codes return null and are not shown. */
export function ruleChipText(r: DealRule): string | null {
  const p = r.params ?? {}
  switch (r.code) {
    case 'no_contact': return 'Nobody linked'
    case 'never_contacted': return 'Never contacted'
    case 'close_passed': return 'Close date passed'
    case 'quiet': return p.days !== undefined ? `Quiet ${p.days} days` : 'Quiet'
    case 'concentration':
      // Floored to one decimal so a deal over 40 % never reads "40%".
      return p.shareBasisPoints !== undefined ? `${Math.floor(p.shareBasisPoints / 10) / 10}% of weighted pipeline` : 'Large share of weighted pipeline'
    case 'single_threaded': return 'One contact'
    case 'no_close_date': return 'No close date'
    case 'stuck_first_stage': return p.days !== undefined ? `${p.days} days in New lead` : 'Stuck in New lead'
    case 'no_decision_maker': return 'No decision maker'
    case 'close_date_unrealistic': return 'Close date unlikely'
    case 'test_data': return 'Looks like a test'
  }
  return null
}

export interface RuleChip { code: string; severity: string; text: string }

/** The chips a deal row shows: the server's order (worst first), every rule
 *  but test_data — a test deal shows ONLY "Looks like a test" — at most
 *  `max`, with the rest counted. A wire without rules[] shows none. */
export function dealRuleChips(rules: readonly DealRule[] | null | undefined, isTest: boolean | undefined, max = 3): { shown: RuleChip[]; more: RuleChip[] } {
  if (!rules || rules.length === 0) return { shown: [], more: [] }
  const all = rules
    .filter(r => (isTest ? r.code === 'test_data' : r.code !== 'test_data'))
    .map(r => ({ code: r.code, severity: r.severity, text: ruleChipText(r) }))
    .filter((c): c is RuleChip => c.text !== null)
  return { shown: all.slice(0, max), more: all.slice(max) }
}

/** A chip's colour from its SEVERITY only (D5): bad → danger, warn →
 *  warning, info → none (the outline capsule). */
export function ruleTone(severity: string): { background: string; color: string } | null {
  if (severity === 'bad') return { background: danger.bg, color: danger.text }
  if (severity === 'warn') return { background: warning.bg, color: warning.text }
  return null
}

// ── The account health card (spec §3) ────────────────────────────────────────

export const CHECK_LABELS: Record<string, string> = {
  website: 'Website',
  owner: 'Owner',
  people_coverage: 'People coverage',
  any_conversation: 'Any conversation',
  next_step: 'Next step',
  close_date: 'Close date realistic',
}

export function statusWords(status: string): string {
  if (status === 'at_risk') return 'At risk'
  if (status === 'watch') return 'Watch'
  if (status === 'healthy') return 'Healthy'
  return status
}

export function statusTone(status: string): { background: string; color: string } | null {
  if (status === 'at_risk') return { background: danger.bg, color: danger.text }
  if (status === 'watch') return { background: warning.bg, color: warning.text }
  if (status === 'healthy') return { background: success.bg, color: success.text }
  return null
}

/** The ring's stroke: the badge's hue. */
export function statusStroke(status: string): string {
  if (status === 'at_risk') return danger.DEFAULT
  if (status === 'watch') return warning.DEFAULT
  return success.DEFAULT
}

/** "Fri 29 Jan" from a YYYY-MM-DD day (the server's), with the year when it
 *  is not the year of `today` (YYYY-MM-DD). */
export function shortDay(day: string | undefined, today: string): string {
  const m = day ? /^(\d{4})-(\d{2})-(\d{2})/.exec(day) : null
  if (!m) return ''
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  const base = `${WEEKDAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`
  return m[1] === today.slice(0, 4) ? base : `${base} ${m[1]}`
}

/** One check's right-hand words (spec §3's "Rolls-Royce today" column). */
export function checkValue(c: SalesAccountHealthCheck, ageDays: number | null): string {
  const p = c.params ?? {}
  if (c.state === 'not_applicable') {
    return p.valueUnconverted ? 'Value not converted to GBP' : 'No open deal'
  }
  const pass = c.state === 'pass'
  switch (c.id) {
    case 'website': return pass ? (p.domain ?? '') : 'No website set'
    case 'owner': return pass ? (p.ownerName ?? '') : 'No owner on the largest open deal'
    case 'people_coverage': return `${p.linked ?? 0} of ${p.needed ?? 0}`
    case 'any_conversation': {
      if (pass) return n(p.touchCount ?? 0, 'call, email or meeting', 'calls, emails or meetings')
      const since = ageDays === null ? 'None yet' : `None in ${n(ageDays, 'day', 'days')}`
      return p.stage ? `${since}, yet the deal is ${stageWords(p.stage)}` : since
    }
    case 'next_step': return pass ? `${p.nextStepCount ?? 0} planned` : 'Nothing planned'
    case 'close_date':
      if (pass) return 'Realistic'
      return `${n(p.stagesLeft ?? 0, 'stage', 'stages')} to go in ${n(p.daysToClose ?? 0, 'day', 'days')}`
  }
  return ''
}

/** The headline (Q7): from the server's headline check, worded here; "All
 *  … pass" when none fails. */
export function healthHeadline(h: SalesAccountHealth, accountName: string, today: string): string {
  const c = (h.checks ?? []).find(x => x.id === h.headline)
  if (!h.headline || !c) {
    return h.applicable === 6 ? 'All six checks pass' : `All ${h.applicable} checks that apply pass`
  }
  const p = c.params ?? {}
  switch (c.id) {
    case 'any_conversation':
      return p.stage ? `${stageWords(p.stage)} on paper, but nobody has spoken to ${accountName}` : `Nobody has spoken to ${accountName} yet`
    case 'people_coverage':
      return `${p.dealName} has ${p.linked ?? 0} of the ${p.needed ?? 0} people a ${compactMoney(p.dealValuePence ?? 0, 'GBP')} deal needs`
    case 'close_date':
      return `${p.dealName} is unlikely to close by ${shortDay(p.expectedClose, today)}`
    case 'next_step': return `Nothing is planned next with ${accountName}`
    case 'owner': return `Nobody owns ${p.dealName}`
    case 'website': return `${accountName} has no website set`
  }
  return ''
}

const SEATS = ['decision maker', 'budget owner', 'procurement']

/** The coverage footer (spec §8): "A £3.0M deal needs 3 people: decision
 *  maker, budget owner and procurement." Null when no tier applies. */
export function coverageFooter(c: SalesAccountHealthCheck | undefined): string | null {
  const p = c?.params
  if (!c || c.state === 'not_applicable' || !p?.needed || p.dealValuePence === undefined) return null
  const seats = SEATS.slice(0, p.needed)
  const list = seats.length === 1 ? seats[0] : `${seats.slice(0, -1).join(', ')} and ${seats[seats.length - 1]}`
  return `A ${compactMoney(p.dealValuePence, 'GBP')} deal needs ${n(p.needed, 'person', 'people')}: ${list}.`
}

/** AD4 (spec v3 §8; design note §45 Privacy): each people seat's words and
 *  the job title "Find" searches for. "Usually the CFO." and "Chief Financial
 *  Officer" are the spec's; the decision maker and procurement words follow
 *  the same pattern. */
export const SEAT_INFO: Record<SalesAccountSeat['seat'], { label: string; usually: string; searchTitle: string }> = {
  decision_maker: { label: 'Decision maker', usually: 'Usually the CEO.', searchTitle: 'Chief Executive Officer' },
  budget_owner: { label: 'Budget owner', usually: 'Usually the CFO.', searchTitle: 'Chief Financial Officer' },
  procurement: { label: 'Procurement', usually: 'Usually the head of procurement.', searchTitle: 'Head of Procurement' },
}

/** The seats of the people_coverage check, or [] (no tier, no open deal, or
 *  an api older than AD4). */
export function coverageSeats(c: SalesAccountHealthCheck | undefined): SalesAccountSeat[] {
  if (!c || c.state === 'not_applicable') return []
  return c.params?.seats ?? []
}

/** The People card's head (AD4 ruling, Klemen 2026-11-01): both facts —
 *  "<n> people · <k> of <N> roles filled". n is the people linked to the deal
 *  (the AD3 coverage count, any role); k of N are the seats filled, N by the
 *  deal's tier. "person" when n is 1. */
export function peopleHead(linked: number, seats: readonly SalesAccountSeat[]): string {
  const filled = seats.filter(x => x.filled).length
  return `${linked} ${linked === 1 ? 'person' : 'people'} · ${filled} of ${seats.length} roles filled`
}

/** "Find" (design note §45 Privacy): an EXTERNAL web search the user's own
 *  browser opens in a new tab — the query "<job title> <domain>". Klaxiora
 *  fetches, scrapes and stores nothing; this is only a link. Null without a
 *  domain (nothing honest to search for). */
export function seatSearchUrl(seat: SalesAccountSeat['seat'], domain: string | undefined): string | null {
  const d = (domain ?? '').trim()
  if (!d) return null
  return `https://www.google.com/search?q=${encodeURIComponent(`${SEAT_INFO[seat].searchTitle} ${d}`)}`
}

/** The empty seat's hint (spec §8): "Usually the CFO. ✦ Search rolls-royce.com
 *  for 'Chief Financial Officer'". Without a domain, the first sentence and a
 *  pointer to the missing website. */
export function seatHint(seat: SalesAccountSeat['seat'], domain: string | undefined): string {
  const info = SEAT_INFO[seat]
  const d = (domain ?? '').trim()
  return d ? `${info.usually} ✦ Search ${d} for '${info.searchTitle}'` : `${info.usually} Add the website to search for one.`
}

/** The ✦ Close date check's sentence (spec §4). */
export function closeCheckSentence(c: SalesAccountHealthCheck, today: string): string {
  const p = c.params ?? {}
  const median = p.medianDays ?? 0
  const per = median >= 28 && median <= 31 ? 'a month' : n(median, 'day', 'days')
  const how = p.medianFallback
    ? `With no won deals to learn from yet, each stage is taken as about ${per}`
    : `Your stages usually take about ${per} each`
  return `${stageWords(p.stage)} with ${n(p.stagesLeft ?? 0, 'stage', 'stages')} left and ${n(p.daysToClose ?? 0, 'day', 'days')} to ${shortDay(p.expectedClose, today)}. ${how}, so ${shortDay(p.suggestedClose, today)} is realistic.`
}
